"use client";

import { useId, useState } from "react";
import { ChevronDownIcon, InfoIcon } from "lucide-react";
import Link from "next/link";
import { cn } from "@/lib/utils";
import type { PackageSummary } from "../pricing";
import { ManageYesNoStep } from "./ManageYesNoStep";

/**
 * The explainer panel above the choice.
 *
 * Collapsible, but open by default: the export only ever draws it expanded, and
 * the distinction it explains is what the question below actually turns on.
 */
function ReturnsExplainer() {
  const [open, setOpen] = useState(true);
  const panelId = useId();

  return (
    <div className="w-full rounded-2xl bg-[#161B22] p-5 shadow-[0px_8px_24px_rgba(0,0,0,0.25)] outline outline-1 -outline-offset-1 outline-[#30363D] sm:p-8">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
        aria-controls={panelId}
        className="flex w-full items-center gap-3 text-left sm:gap-4"
      >
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#21262D]">
          <InfoIcon aria-hidden className="h-4 w-4 text-[#58A6FF]" />
        </span>
        <span className="flex-1 font-inter text-base font-semibold leading-snug text-[#F0F6FC] sm:text-lg sm:leading-[25.2px]">
          Difference between annual and profit tax returns
        </span>
        <ChevronDownIcon
          aria-hidden
          className={cn(
            "h-5 w-5 shrink-0 text-[#F0F6FC] transition-transform",
            open && "rotate-180",
          )}
        />
      </button>

      <div id={panelId} hidden={!open}>
        <div className="my-5 h-px w-full bg-[#30363D]" />
        <div className="flex flex-col gap-4">
          <p className="font-inter text-[15px] font-medium leading-[22.5px] text-[#C9D1D9]">
            An annual return and its profit tax returns are different and unrelated.
          </p>
          <p className="font-inter text-sm leading-[22.4px] text-[#8B949E]">
            An annual return is a form (NAR1) prepared by a company secretary and submitted to the
            Companies Registry to report structural details like directors and shareholders.
          </p>
          <p className="font-inter text-sm leading-[22.4px] text-[#8B949E]">
            A profit tax return is a document required for reporting a company’s assessable profits
            to the Inland Revenue Department (IRD).
          </p>
          <p className="font-inter text-sm font-medium leading-[22.4px] text-[#8B949E]">
            Learn more about the differences{" "}
            {/* The export gives no destination — pointed at the resources index
                until the actual article URL is known. */}
            <Link href="/blogs" className="text-[#58A6FF] underline">
              here.
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

/** Company-secretary side: outstanding annual returns for past years. */
export function ManagePastReturnsStep({
  value,
  onChange,
  summary,
  onNext,
}: {
  value: string | null;
  onChange: (value: string) => void;
  summary: PackageSummary;
  onNext: () => void;
}) {
  return (
    <ManageYesNoStep
      heading="Need help with annual returns from previous years?"
      intro={
        <>
          {/* The export's line break is kept from sm up and dropped on phones,
              where the text needs to reflow. */}
          As your new company secretary, we can assist with filing outstanding{" "}
          <br className="max-sm:hidden" />
          annual returns to ensure compliance.
        </>
      }
      name="past-annual-returns"
      value={value}
      onChange={onChange}
      summary={summary}
      onNext={onNext}
    >
      <ReturnsExplainer />
    </ManageYesNoStep>
  );
}
